import React from "react"
import {useTranslation} from "react-i18next"
import {XMarkIcon} from "@heroicons/react/20/solid"

import {useSalesAssistantSalesPeopleSelectBoxQuery} from "../../../queries/salesAssistant.ts"
import {getFullName} from "../../../utils/index.ts"
import {KlaraFiltersContext} from "../filters.tsx"

/** The active sales-person filter as a "Name ✕" chip; clicking it clears the filter. Hidden without a filter. */
export const PersonChip: React.FC = () => {
  const {salesPersonExternalId, setSalesPersonExternalId} = KlaraFiltersContext.useContext()

  if (!salesPersonExternalId) {
    return null
  }

  return <PersonChipButton externalId={salesPersonExternalId} onClear={() => setSalesPersonExternalId(undefined)} />
}

const PersonChipButton: React.FC<{externalId: string; onClear: () => void}> = ({externalId, onClear}) => {
  const {t} = useTranslation()
  const {data, isPending} = useSalesAssistantSalesPeopleSelectBoxQuery()

  // The chip is clearable before the name arrives; an id from an old link may match no one at all.
  const salesPerson = data?.data.sales_people?.find(person => person.external_id === externalId)
  const name = isPending
    ? t("KlaraDashboard_PersonChip_Pending")
    : getFullName(salesPerson, t("KlaraDashboard_UpdateCenter_UnknownSalesPerson"))

  return (
    <button
      type={"button"}
      onClick={onClear}
      aria-label={t("KlaraDashboard_PersonChip_Clear", {name})}
      className={
        "inline-flex max-w-48 items-center gap-1 rounded-full bg-cr-blue-light py-1 pr-2 pl-2.5 text-[11.5px] font-semibold text-cr-blue-dark hover:bg-cr-blue-mid-light"
      }
    >
      <span className={"truncate"}>{name}</span>
      <XMarkIcon className={"size-4 shrink-0"} aria-hidden={"true"} />
    </button>
  )
}
